import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { formatNumberToLocale, truncateToDecimals } from '../../helpers/numberFormatter';
import { getQuantityForPrice, getPriceForQuantity, getOrdersBySide, getMarketPrice, getValuesFromDealValues } from '../../helpers/orderHelper';
import { getAvailableProductBalance } from '../../helpers/productHelper';
import { selectedInstrumentSelector } from '../selectors/instrumentPositionSelectors';
import { kycVerificationLevelsSelector } from '../selectors/kycLevelsSelectors';
import { BUY_SELL_LAYOUTS } from '../../components/Retail/RetailBuySell/BuySell/BuySellLayoutTypes';
import { buyValue } from '../../constants/sendOrder/orderEntryFormConstants';
import config from '../../config';
import { getProductByIdSelector } from './productPositionSelectors';
import { convertIncrementToIntDecimalPlaces } from '../../helpers/decimalPlaces/decimalPlacesHelper';
export var marketPriceOfSelectedInstrumentSelector = createSelector([selectedInstrumentSelector, function (state) {
  return state.apexCore.level1;
}, function (state, options) {
  return options;
}], function (instrument, level1, options) {
  var side = options.side,
    formatToLocale = options.formatToLocale;
  var formatToLocaleOption = formatToLocale === undefined || formatToLocale;
  var l1 = instrument ? level1[instrument.InstrumentId] : null;
  var marketPrice = getMarketPrice(side, instrument, l1, convertIncrementToIntDecimalPlaces(instrument.PriceIncrement));
  if (instrument && formatToLocaleOption) {
    return formatNumberToLocale(marketPrice, convertIncrementToIntDecimalPlaces(instrument.PriceIncrement));
  }
  return marketPrice;
});
export var isBuySideBuySellFormSelector = createSelector([function (state) {
  return state.form.buySell;
}], function (formObj) {
  return formObj ? formObj.values.side === buyValue : true;
});

/**
 * accept array of prices values and return price, quantity, limitPrice and pricePerUnit for them
 */
export var getPriceDataForPricesSelector = createSelector([function (state) {
  return state.apexCore.level2;
}, function (state) {
  return state.apexCore.product;
}, function (state, side) {
  return side;
}, function (state, side, prices) {
  return prices;
}, selectedInstrumentSelector], function (level2, product, side, prices, selectedInstrument) {
  var decimalPlaces = product.decimalPlaces;
  var _config$OrderCalcBuff = config.OrderCalcBuffer;
  _config$OrderCalcBuff = _config$OrderCalcBuff === undefined ? {} : _config$OrderCalcBuff;
  var quantityBuffer = _config$OrderCalcBuff.quantityBuffer;
  var orders = getOrdersBySide(side, level2);
  return prices.map(function (price) {
    var _getQuantityForPrice = getQuantityForPrice(price, quantityBuffer, orders, true, side),
      Quantity = _getQuantityForPrice.Quantity,
      LimitPrice = _getQuantityForPrice.LimitPrice;
    var quantity = truncateToDecimals(Quantity, convertIncrementToIntDecimalPlaces(selectedInstrument.QuantityIncrement)).toNumber();
    var pricePerUnit = quantity > 0 ? truncateToDecimals(price / quantity, convertIncrementToIntDecimalPlaces(selectedInstrument.PriceIncrement)).toNumber() : null;
    return {
      price: price,
      quantity: quantity,
      limitPrice: truncateToDecimals(LimitPrice, convertIncrementToIntDecimalPlaces(selectedInstrument.PriceIncrement)).toNumber(),
      pricePerUnit: pricePerUnit
    };
  });
});

/**
 * return array of object of format {price, quantity, limitPrice and pricePerUnit} for preset values
 */
export var getPriceDataForPresetValuesSelector = createSelector([function (state) {
  return state;
}, function (state, buySell) {
  return buySell;
}, selectedInstrumentSelector], function (state, side, selectedInstrument) {
  var dealPrices = config.RetailBuySell.dealPrices;
  var prices = dealPrices[selectedInstrument.Symbol];
  if (!prices) {
    prices = dealPrices['DEFAULT'];
  }
  return getPriceDataForPricesSelector(state, side, prices);
});

/**
 * return array of object of format {price, quantity, limitPrice and pricePerUnit}
 * for preset values and custom row
 */
export var getPriceDataForPresetAndCustomValuesSelector = createSelector([function (state) {
  return state;
}, function (state, buySell) {
  return buySell;
}, function (state, buySell, customPrice) {
  return customPrice;
}, selectedInstrumentSelector], function (state, side, customPrice, selectedInstrument) {
  var dealPrices = config.RetailBuySell.dealPrices;
  var prices = dealPrices[selectedInstrument.Symbol];
  if (!prices) {
    prices = dealPrices['DEFAULT'];
  }
  var totalPrices = [customPrice].concat(prices);
  return getPriceDataForPricesSelector(state, side, totalPrices);
});

/**
 * accept array of quantity values and return quantity, price, limitPrice and pricePerUnit for them
 */
export var getPriceDataForQuantitiesSelector = createSelector([function (state) {
  return state.apexCore.level2;
}, function (state) {
  return state.apexCore.product;
}, function (state, side) {
  return side;
}, function (state, side, quantities) {
  return quantities;
}, selectedInstrumentSelector], function (level2, product, side, quantities, selectedInstrument) {
  var decimalPlaces = product.decimalPlaces;
  var _config$OrderCalcBuff2 = config.OrderCalcBuffer;
  _config$OrderCalcBuff2 = _config$OrderCalcBuff2 === undefined ? {} : _config$OrderCalcBuff2;
  var priceBuffer = _config$OrderCalcBuff2.priceBuffer;
  var orders = getOrdersBySide(side, level2);
  return quantities.map(function (quantity) {
    var _getPriceForQuantity = getPriceForQuantity(quantity, priceBuffer, orders, false, side),
      Price = _getPriceForQuantity.Price,
      LimitPrice = _getPriceForQuantity.LimitPrice;
    var price = truncateToDecimals(Price, convertIncrementToIntDecimalPlaces(selectedInstrument.QuantityIncrement)).toNumber();
    var pricePerUnit = price / quantity;
    pricePerUnit = quantity > 0 && pricePerUnit ? formatNumberToLocale(pricePerUnit, convertIncrementToIntDecimalPlaces(selectedInstrument.PriceIncrement)).toString() : null;
    return {
      price: price,
      quantity: quantity,
      limitPrice: truncateToDecimals(LimitPrice, convertIncrementToIntDecimalPlaces(selectedInstrument.PriceIncrement)).toNumber(),
      pricePerUnit: pricePerUnit
    };
  });
});

/**
 * return array of object of format {quantity, price, limitPrice and pricePerUnit} for preset values
 */
export var getQuantityDataForPresetValuesSelector = createSelector([function (state) {
  return state;
}, function (state, buySell) {
  return buySell;
}, selectedInstrumentSelector], function (state, side, selectedInstrument) {
  var dealQuantities = config.RetailBuySell.dealPrices;
  var quantities = dealQuantities[selectedInstrument.Symbol];
  if (!quantities) {
    return [];
  }
  return getPriceDataForQuantitiesSelector(state, side, quantities);
});

/**
 * return array of object of format {quantity, price, limitPrice and pricePerUnit}
 * for preset values and custom row
 */
export var getQuantityDataForPresetAndCustomValuesSelector = createSelector([function (state) {
  return state;
}, function (state, buySell) {
  return buySell;
}, function (state, buySell, customQuantity) {
  return customQuantity;
}, selectedInstrumentSelector], function (state, side, customQuantity, selectedInstrument) {
  var dealQuantities = config.RetailBuySell.dealPrices;
  var quantities = dealQuantities[selectedInstrument.Symbol];
  if (!quantities) {
    return [];
  }
  var totalQuantities = [customQuantity].concat(quantities);
  return getPriceDataForQuantitiesSelector(state, side, totalQuantities);
});

// responsible to return price, quantity, limitPrice, pricePerUnit
// from current state of buySell form depending on layoutType,
// what is selected (custom radio section or initally set values)
export var getOrderFormInfoSelector = createSelector([function (state) {
  return state;
}, function (state) {
  return state.form.buySell || {
    values: {}
  };
}], function (state, form) {
  var _form$values = form.values,
    side = _form$values.side,
    amountRadio = _form$values.amountRadio;
  var isPricePerUnitLayoutType = form.values.layoutType === BUY_SELL_LAYOUTS.PRICE_PER_UNIT;
  var fieldToSearch = isPricePerUnitLayoutType ? 'quantity' : 'price';
  var value = amountRadio === 'custom' ? form.values[fieldToSearch] : amountRadio;
  var dealValues = isPricePerUnitLayoutType ? getQuantityDataForPresetAndCustomValuesSelector(state, side, value) : getPriceDataForPresetAndCustomValuesSelector(state, side, value);
  return getValuesFromDealValues(dealValues, value, fieldToSearch);
});
export var tradingDisabledSelector = createSelector([function (state) {
  return state;
}], function (state) {
  return kycVerificationLevelsSelector(state).disableTrading;
});
export var noBalanceSelector = createSelector([function (state) {
  return state;
}, function (state) {
  return isBuySideBuySellFormSelector(state);
}, function (state) {
  return state.form.buySell || {
    values: {}
  };
}], function (state, isBuySide, form) {
  var selector = formValueSelector('buySell');
  var selectedInstrument = selectedInstrumentSelector(state);
  var product1 = getProductByIdSelector(state, selectedInstrument.Product1);
  var product2 = getProductByIdSelector(state, selectedInstrument.Product2);
  var selectedValue = getOrderFormInfoSelector(state);
  var activeAmount = selector(state, 'amountRadio');
  var price = activeAmount === 'custom' ? form.values.price : +activeAmount;
  var quantity = selectedValue.quantity;
  var negativeIsNaN = false;
  return isBuySide ? getAvailableProductBalance(product2, negativeIsNaN) < price : getAvailableProductBalance(product1, negativeIsNaN) < quantity;
});